import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BRAND } from "./constants";
import { HeaderBar, HeaderDetails } from "./components/Header.jsx";
import SearchBar from "./components/SearchBar.jsx";
import TributeWall from "./components/TributeWall.jsx";
import Footer from "./components/Footer.jsx";
import ExpandedCard from "./components/ExpandedCard.jsx";
import StepUpFlow from "./components/StepUpFlow.jsx";
import AdminPage from "./components/AdminPage.jsx";
import NewTributePoc from "./components/NewTributePoc.jsx";
import "./App.css";

var pathname = window.location.pathname;

function TributeApp() {
  var headerBarRef = useRef(null);
  var [headerBarHeight, setHeaderBarHeight] = useState(0);
  var [search, setSearch] = useState("");
  var [schoolFilter, setSchoolFilter] = useState("All");
  var [expandedId, setExpandedId] = useState(null);
  var [expandedIndex, setExpandedIndex] = useState(0);
  var [isDeepLink, setIsDeepLink] = useState(false);
  var [tributes, setTributes] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showStepUp, setShowStepUp] = useState(false);
  var [showTributeOnly, setShowTributeOnly] = useState(false);

  useEffect(function () {
    if (!headerBarRef.current) return;
    var ro = new ResizeObserver(function () {
      if (headerBarRef.current) {
        setHeaderBarHeight(headerBarRef.current.offsetHeight);
      }
    });
    ro.observe(headerBarRef.current);
    setHeaderBarHeight(headerBarRef.current.offsetHeight);
    return function () { ro.disconnect(); };
  }, [loading]);

  // Fetch tributes from Netlify Blobs API (updated by admin Publish).
  // If ?preview=<file>&password=<pw> is in the URL, fetch that snapshot instead.
  var [previewMode, setPreviewMode] = useState(false);
  function loadTributes(data) {
    if (data.tributes && data.tributes.length > 0) {
      var sorted = data.tributes.slice().sort(function (a, b) {
        var da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        var db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      setTributes(sorted);
    } else {
      setTributes([]);
    }
    setLoading(false);
  }
  useEffect(function () {
    var params = new URLSearchParams(window.location.search);
    var previewFile = params.get("preview");
    var pw = params.get("password");

    if (previewFile && pw) {
      setPreviewMode(true);
      fetch("/api/publish?action=preview&file=" + encodeURIComponent(previewFile) + "&password=" + encodeURIComponent(pw))
        .then(function (res) {
          if (!res.ok) throw new Error("Preview fetch failed: " + res.status);
          return res.json();
        })
        .then(loadTributes)
        .catch(function (err) {
          console.warn("Preview load failed:", err.message);
          setTributes([]);
          setLoading(false);
        });
      return;
    }

    fetch("/api/tributes-data")
      .then(function (res) {
        if (!res.ok) throw new Error("API returned " + res.status);
        return res.json();
      })
      .then(loadTributes)
      .catch(function (err) {
        console.warn("Could not load tributes:", err.message);
        setTributes([]);
        setLoading(false);
      });
  }, []);

  // Deep link handling — runs after tributes are loaded
  useEffect(function () {
    if (loading) return;
    var params = new URLSearchParams(window.location.search);
    var tributeId = params.get("tribute");
    var teacher = params.get("teacher");
    if (tributeId) {
      // Use string comparison for IDs (tribute-data.json uses string IDs)
      var found = tributes.find(function (t) { return String(t.id) === String(tributeId); });
      if (found) {
        setExpandedId(found.id);
        setIsDeepLink(true);
      }
    }
    if (teacher) setSearch(decodeURIComponent(teacher));
  }, [loading, tributes]);

  var handleTributesSubmitted = function () {
    /* Tributes go to moderation queue — don't add to wall immediately */
  };

  var filtered = tributes.filter(function (t) {
    if (schoolFilter !== "All" && t.school !== schoolFilter) return false;
    if (!search) return true;
    var q = search.toLowerCase();
    return t.teacher.toLowerCase().includes(q) || t.from.toLowerCase().includes(q);
  });

  // Determine which tributes are "new":
  // Any within 24h, or the 2 most recent if none are within 24h
  var newIds = useMemo(function () {
    if (tributes.length === 0) return new Set();
    var DAY_MS = 24 * 60 * 60 * 1000;
    var now = Date.now();
    var recentIds = [];
    tributes.forEach(function (t) {
      if (!t.createdAt) return;
      var created = new Date(t.createdAt).getTime();
      if (!isNaN(created) && (now - created) < DAY_MS) {
        recentIds.push(t.id);
      }
    });
    if (recentIds.length > 0) return new Set(recentIds);
    // No tributes within 24h — highlight the 2 most recent
    // Tributes are already sorted newest first
    return new Set(tributes.slice(0, 2).map(function (t) { return t.id; }));
  }, [tributes]);

  var expandedTribute = expandedId ? tributes.find(function (t) { return t.id === expandedId; }) : null;

  var handleCardClick = useCallback(function (id, index) { setExpandedId(id); setExpandedIndex(index); setIsDeepLink(false); }, []);
  var handleCloseExpanded = useCallback(function () { setExpandedId(null); setIsDeepLink(false); }, []);
  var handleNavigate = useCallback(function (id, index) { setExpandedId(id); if (index !== undefined) setExpandedIndex(index); }, []);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.warm, fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: BRAND.textLight }}>
          <div style={{ width: "32px", height: "32px", border: "3px solid " + BRAND.primaryLight, borderTopColor: BRAND.primary, borderRadius: "50%", animation: "spin 0.6s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ fontSize: "14px" }}>Loading tributes...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.warm, fontFamily: "'DM Sans', sans-serif" }}>
      <div ref={headerBarRef} style={{ position: "sticky", top: 0, zIndex: 11 }}>
        <HeaderBar />
      </div>

      <HeaderDetails />

      <div style={{ position: "sticky", top: headerBarHeight + "px", zIndex: 10, background: BRAND.warm }}>
        <SearchBar
          search={search}
          onSearchChange={setSearch}
          schoolFilter={schoolFilter}
          onSchoolFilterChange={setSchoolFilter}
          resultCount={filtered.length}
          hasFilters={!!search || schoolFilter !== "All"}
        />
      </div>

      <TributeWall
        tributes={filtered}
        onCardClick={handleCardClick}
        newIds={newIds}
      />

      <Footer />

      {/* Expanded card overlay */}
      {expandedTribute && (
        <ExpandedCard
          tribute={expandedTribute}
          onClose={handleCloseExpanded}
          allTributes={tributes}
          onNavigate={handleNavigate}
          cardIndex={expandedIndex}
          skipAnimation={isDeepLink}
        />
      )}

      {/* Step Up Flow */}
      {showStepUp && (
        <StepUpFlow
          onClose={function () { setShowStepUp(false); }}
          onTributesSubmitted={handleTributesSubmitted}
          skipToTributes={false}
        />
      )}
      {showTributeOnly && (
        <StepUpFlow
          onClose={function () { setShowTributeOnly(false); }}
          onTributesSubmitted={handleTributesSubmitted}
          skipToTributes={true}
        />
      )}
    </div>
  );
}

export default function App() {
  if (pathname === "/admin") return <AdminPage />;
  if (pathname === "/poc") return <NewTributePoc />;
  return <TributeApp />;
}
